import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import Header from 'components/Common/Header';
import Tab from 'components/Common/Tab';
import TextInput from 'components/Common/TextInput';
import Table from 'components/Common/StyledTable';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, IconButton, Tooltip } from '@mui/material';
import routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { getStaffList, getInvitations, revokeStaffInvitation } from 'redux/slices/staff/staffSlice';
import { selectSelectedParishId, selectEffectiveStaffRole } from 'redux/selectors/parishContext';
import { hasAtLeastRole } from 'api/v1Helpers';
import MuiLoader from 'components/Common/Loader';
import type { StaffMember, Invitation } from 'services/staffApi';
import { updateTableState } from 'redux/slices/common/appSlice';
import { defaultPageSize } from 'appConfig';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});

type TabValue = 'STAFF' | 'INVITATIONS';

const staffColumns = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'role', label: 'Role' },
];

const baseInvitationColumns = [
    { id: 'email', label: 'Email' },
    { id: 'role', label: 'Role' },
    { id: 'status', label: 'Status' },
];

function formatStaffRow(s: StaffMember) {
    const name = [s.user.firstName, s.user.lastName].filter(Boolean).join(' ') || s.user.email;
    return { id: s.user.id, name, email: s.user.email, role: s.role, raw: s };
}

function formatInvitationRow(inv: Invitation) {
    return { id: inv.id, email: inv.email, role: inv.role, status: inv.status, raw: inv };
}

const Staff: React.FC = () => {
    const dispatch = useDispatch<typeof store.dispatch>();
    const navigate = useNavigate();
    const [tab, setTab] = useState<TabValue>('STAFF');
    const [searchValue, setSearchValue] = useState('');

    const parishId = useSelector(selectSelectedParishId);
    const effectiveRole = useSelector(selectEffectiveStaffRole);
    const canManageStaff = hasAtLeastRole(effectiveRole, 'MERCHANT_ADMIN');

    const { items, itemsTotal, loading, error, invitations, invitationsTotal, invitationsLoading } = useSelector(
        (state: RootState) => state.staff.staffList
    );
    const { pageNumber = 0, pageSize = defaultPageSize } = useSelector((state: RootState) => state.app.table) || {};

    const handleRevoke = useCallback(
        (invitationId: string) => {
            if (!parishId) return;
            dispatch(revokeStaffInvitation({ parishId, invitationId }));
        },
        [parishId, dispatch]
    );

    const invitationColumns = useMemo(
        () => [
            ...baseInvitationColumns,
            {
                id: 'action',
                label: '',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- StyledTable's column render row shape
                render: ({ data }: any) =>
                    data?.status === 'PENDING' ? (
                        <Tooltip title="Revoke invitation">
                            <IconButton
                                size="small"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleRevoke(data.id);
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null,
            },
        ],
        [handleRevoke]
    );

    // Parish switch: reset the shared table page back to 0. Staff/Invitations are paginated
    // entirely client-side over the shared table-pagination slice, and (unlike
    // Parishes/Community/Announcements) this screen keeps one persistent Table element across
    // both tabs, so it never unmounts to pick up StyledTable's own reset. A parish switch while
    // sitting on page 2+ can otherwise slice the new (possibly shorter) staff/invitations list
    // with a stale page and show an empty table.
    useEffect(() => {
        if (!parishId) return;
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches
    }, [parishId, dispatch]);

    // Fetch Staff — only while that tab is active, mirroring Community.tsx's per-tab fetch
    // effects (fetchParishMembers/getMemberInvitations), so switching STAFF <-> INVITATIONS
    // visibly re-requests the newly active tab's data instead of relying on both having been
    // eagerly loaded up front. Keyed on pageSize (not pageNumber) — page-to-page navigation
    // stays entirely client-side over this already-loaded list (fetchStaff's backend returns a
    // full set regardless of paging params), so re-fetching per Next/Prev click would be pure
    // waste, but changing rows-per-page should still visibly re-request rather than silently
    // doing nothing.
    useEffect(() => {
        if (!parishId || tab !== 'STAFF') return;
        dispatch(getStaffList({ parishId }));
    }, [parishId, dispatch, tab, pageSize]);

    // Fetch Invitations — same shape, gated on canManageStaff (the only role that can ever
    // select this tab, since it's hidden from `tabs` otherwise). Unlike Staff, this is now
    // genuinely server-paginated (getInvitations reads page/pageSize fresh from
    // state.app.table), so this effect also re-fires on pageNumber — Next/Prev fetches the
    // real next page instead of relying on everything having been loaded up front.
    useEffect(() => {
        if (!parishId || tab !== 'INVITATIONS' || !canManageStaff) return;
        dispatch(getInvitations({ parishId }));
    }, [parishId, dispatch, tab, canManageStaff, pageNumber, pageSize]);

    const staffRows = useMemo(() => (items || []).map(formatStaffRow), [items]);
    const invitationRows = useMemo(() => (invitations || []).map(formatInvitationRow), [invitations]);

    const filteredData = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- staffRows/invitationRows have different shapes
        const source: any[] = tab === 'STAFF' ? staffRows : invitationRows;
        if (!searchValue) return source;
        const q = searchValue.toLowerCase();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see source above
        return source.filter((row: any) => row.name?.toLowerCase().includes(q) || row.email?.toLowerCase().includes(q));
    }, [tab, staffRows, invitationRows, searchValue]);

    // Staff stays paginated entirely client-side (serverSidePagination below is off for that
    // tab) since fetchStaff's backend is documented to ignore paging and always return the full
    // set — but use the real captured itemsTotal (not filteredData.length) when not searching,
    // so a footer mismatch would surface if that assumption is ever wrong rather than silently
    // showing "of <truncated count>". Invitations is now genuinely server-paginated, so it uses
    // its own real backend total the same way. While searching, filteredData only covers what's
    // currently loaded (search has no server-side param on either endpoint), so its length is
    // what's actually shown either way.
    const totalCount = !searchValue ? (tab === 'INVITATIONS' ? invitationsTotal : itemsTotal) : filteredData?.length;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- StyledTable's row shape
    const navigationHandler = (row: any) => {
        if (tab === 'STAFF') {
            navigate(routes.user.userDetail.replace(':username', row.id), {
                state: { type: 'employee', userId: row.id },
            });
        }
    };

    // Invitations tab is permanently hidden: Staff → "+" now provisions accounts immediately
    // (POST .../staff/accounts), so sendStaffInvitation is dead and the tab can never show
    // anything. Left the thunk/service/effect below in place, unused, in case token invitations
    // are ever reintroduced.
    const tabs: { value: TabValue; label: string }[] = [{ value: 'STAFF', label: 'Staff' }];

    const isLoading = tab === 'STAFF' ? loading : invitationsLoading;

    const handleTabChange = (v: TabValue) => {
        setTab(v);
        setSearchValue('');
        // STAFF and INVITATIONS share the global table-pagination slice (see StyledTable) —
        // reset it on every switch so a page-2 staff view doesn't leave the (usually shorter)
        // invitations table sliced into emptiness, and vice versa.
        dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        // A search made while sitting on page 2+ would otherwise slice the (now smaller)
        // filtered result set with a stale page number and show an empty table.
        dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
    };

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader loading={isLoading} absolute />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            {error && !isLoading && (
                <Alert severity="error" sx={{ mx: 2.75 }}>
                    {error}
                </Alert>
            )}
            <StyledActionContainer>
                <StyledInnerContainer>
                    <Card sx={{ mb: 0.5 }}>
                        <div style={{ padding: 16 }}>
                            <TextInput value={searchValue} onChange={handleSearchChange} />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 6,
                                flexDirection: 'row',
                                justifyContent: 'space-around',
                            }}
                        >
                            <Tab tabs={tabs} tabValue={tab} setTabValue={handleTabChange} />
                            {canManageStaff && (
                                <IconButton sx={{ marginRight: 1 }} onClick={() => navigate(routes.staff.add)}>
                                    <AddIcon htmlColor="black" />
                                </IconButton>
                            )}
                        </div>
                    </Card>
                    <Table
                        unStyled
                        navigationHandler={tab === 'STAFF' ? navigationHandler : () => {}}
                        columns={tab === 'STAFF' ? staffColumns : invitationColumns}
                        data={filteredData}
                        totalCount={totalCount}
                        // Invitations is server-paginated (see the fetch effect above); Staff
                        // stays client-paginated since its backend ignores paging entirely.
                        serverSidePagination={tab === 'INVITATIONS'}
                        loading={isLoading}
                    />
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Staff;
